import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useScreenshots, useLiveScreenshotRequest } from '../../hooks/useScreenshots';
import { useAuthStore } from '../../stores/auth.store';
import { devicesApi, activityApi, screenshotsApi } from '../../api/client';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { formatDateTime, formatTime, formatDuration, formatHourRange } from '../../lib/utils';

function DeviceListView({
  selectedDate,
  onDateChange,
  onSelectDevice,
  isManager,
}: {
  selectedDate: string;
  onDateChange: (date: string) => void;
  onSelectDevice: (deviceId: string, name: string, employeeName?: string) => void;
  isManager: boolean;
}) {
  const { data: devices, isLoading: loadingDevices } = useQuery({
    queryKey: ['devices-list'],
    queryFn: async () => {
      const response = isManager ? await devicesApi.list() : await devicesApi.getMyDevices();
      return response.data;
    },
  });

  // Employees with exactly one device skip the picker and land straight on
  // their own screenshots view. Managers, and employees with 0 or 2+
  // devices, see the picker unchanged.
  useEffect(() => {
    if (!isManager && devices?.length === 1) {
      const d = devices[0];
      onSelectDevice(
        d.id,
        d.name || d.hostname || 'My Device',
        d.user ? `${d.user.firstName} ${d.user.lastName}` : undefined
      );
    }
  }, [isManager, devices, onSelectDevice]);

  const getPlatformBadge = (platform: string) => {
    const colors: Record<string, string> = {
      win32: 'bg-blue-100 text-blue-700',
      linux: 'bg-orange-100 text-orange-700',
      darwin: 'bg-gray-100 text-gray-700',
    };
    const labels: Record<string, string> = {
      win32: 'Windows',
      linux: 'Linux',
      darwin: 'macOS',
    };
    return { color: colors[platform] || 'bg-gray-100 text-gray-700', label: labels[platform] || platform };
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Screenshots</h1>
          <p className="text-gray-600">Select a device to view its screenshots</p>
        </div>
        <Input
          type="date"
          value={selectedDate}
          onChange={(e) => onDateChange(e.target.value)}
          className="w-auto"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Devices</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingDevices ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : !devices?.length ? (
            <div className="text-center py-8 text-gray-500">No devices found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4 whitespace-nowrap">Device</th>
                    <th className="text-left py-2 px-4 whitespace-nowrap">User</th>
                    <th className="text-left py-2 px-4 whitespace-nowrap">Platform</th>
                    <th className="text-left py-2 px-4 whitespace-nowrap">Active Time</th>
                    <th className="text-left py-2 px-4 whitespace-nowrap">Activity %</th>
                    <th className="text-left py-2 px-4 whitespace-nowrap">Screenshots</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device: any) => (
                    <DeviceRow
                      key={device.id}
                      device={device}
                      selectedDate={selectedDate}
                      getPlatformBadge={getPlatformBadge}
                      onSelect={() =>
                        onSelectDevice(
                          device.id,
                          device.name || device.hostname || 'Unknown Device',
                          device.user ? `${device.user.firstName} ${device.user.lastName}` : undefined
                        )
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DeviceRow({
  device,
  selectedDate,
  getPlatformBadge,
  onSelect,
}: {
  device: any;
  selectedDate: string;
  getPlatformBadge: (platform: string) => { color: string; label: string };
  onSelect: () => void;
}) {
  const { data: summary } = useQuery({
    queryKey: ['activitySummary', { deviceId: device.id, startDate: selectedDate, endDate: selectedDate }],
    queryFn: async () => {
      const response = await activityApi.getSummary({
        deviceId: device.id,
        startDate: selectedDate,
        endDate: selectedDate,
      });
      return response.data;
    },
  });

  const { data: screenshotData } = useQuery({
    queryKey: ['screenshots', { deviceId: device.id, startDate: selectedDate, endDate: selectedDate, limit: 1 }],
    queryFn: async () => {
      const response = await screenshotsApi.list({
        deviceId: device.id,
        startDate: selectedDate,
        endDate: selectedDate,
        limit: 1,
      });
      return response.data;
    },
  });

  const platform = getPlatformBadge(device.platform || '');
  const userName = device.user
    ? `${device.user.firstName} ${device.user.lastName}`
    : '-';

  return (
    <tr
      className="border-b last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={onSelect}
    >
      <td className="py-3 px-4">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center text-white mr-3">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="2" y="3" width="20" height="14" rx="2" strokeWidth="2" />
              <path d="M8 21h8M12 17v4" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <span className="font-medium">{device.name || device.hostname || 'Unknown'}</span>
            {device.hostname && device.name && device.name !== device.hostname && (
              <p className="text-xs text-gray-400">{device.hostname}</p>
            )}
          </div>
        </div>
      </td>
      <td className="py-3 px-4 text-gray-700">{userName}</td>
      <td className="py-3 px-4">
        <span className={`px-2 py-1 rounded text-xs font-medium ${platform.color}`}>
          {platform.label}
        </span>
      </td>
      <td className="py-3 px-4 text-gray-700">
        {summary?.totalActiveSeconds != null
          ? formatDuration(summary.totalActiveSeconds)
          : '-'}
      </td>
      <td className="py-3 px-4 text-gray-700">
        {summary?.averageActivityPercent != null
          ? `${Math.round(summary.averageActivityPercent)}%`
          : '-'}
      </td>
      <td className="py-3 px-4 text-gray-700">
        {screenshotData?.total ?? '-'}
      </td>
    </tr>
  );
}

// ─── Screenshot Detail Sub-Components ──────────────────────────

/** Colored productivity dashes based on percentage */
function ProductivityDashes({ percent }: { percent: number }) {
  const totalDashes = 10;
  const getDashColor = (idx: number, pct: number) => {
    const filled = Math.ceil((pct / 100) * totalDashes);
    if (idx >= filled) return 'bg-gray-300';
    if (pct >= 70) return 'bg-green-500';
    if (pct >= 40) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  return (
    <div className="flex items-center gap-[3px]">
      {Array.from({ length: totalDashes }, (_, i) => (
        <div
          key={i}
          className={`w-4 h-[5px] rounded-sm ${getDashColor(i, percent)}`}
        />
      ))}
    </div>
  );
}

function dateKeyOf(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

/**
 * Small popover month-grid, styled to match this page's own existing
 * chrome (gray-100/200/500/700, rounded, text-sm) rather than the
 * separate pixel-value design system used on the redesigned Dashboard —
 * this page never uses that system elsewhere, so introducing it here
 * for just one control would look inconsistent with its own surroundings.
 * Purely additive: the existing prev/next-day arrows next to it are
 * untouched and still work exactly as before.
 */
function MiniDatePicker({
  selectedDate,
  onSelect,
}: {
  selectedDate: string;
  onSelect: (date: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = new Date(selectedDate + 'T00:00:00');
  const [viewYear, setViewYear] = useState(selected.getFullYear());
  const [viewMonth, setViewMonth] = useState(selected.getMonth());

  // Re-sync the visible month whenever the popover is (re)opened, so it
  // always starts on the currently-selected date's month rather than
  // wherever it was last left after a previous open/close.
  useEffect(() => {
    if (isOpen) {
      const d = new Date(selectedDate + 'T00:00:00');
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  }, [isOpen, selectedDate]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const todayKey = dateKeyOf(new Date());

  const cells = useMemo(() => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    // Monday-start week, matching AttendanceOverview's convention elsewhere in the app.
    const firstWeekday = (firstOfMonth.getDay() + 6) % 7;

    const result: Array<{ key: string; day: number } | null> = [];
    for (let i = 0; i < firstWeekday; i++) result.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      result.push({ key: dateKeyOf(new Date(viewYear, viewMonth, day)), day });
    }
    return result;
  }, [viewYear, viewMonth]);

  const changeMonth = (delta: number) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-label="Choose a date"
        aria-expanded={isOpen}
        className="flex items-center gap-1.5 px-2 py-1 hover:bg-gray-100 rounded transition-colors"
      >
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="2" />
          <path d="M16 2v4M8 2v4M3 10h18" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <span className="text-sm font-medium text-gray-700">
          {selected.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 z-20 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => changeMonth(-1)}
              aria-label="Previous month"
              className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M15 19l-7-7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span className="text-sm font-semibold text-gray-900">{monthLabel}</span>
            <button
              type="button"
              onClick={() => changeMonth(1)}
              aria-label="Next month"
              className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M9 5l7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAY_LABELS.map((label) => (
              <div key={label} className="text-center text-xs font-medium text-gray-400 py-1">
                {label}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {cells.map((cell, i) =>
              cell === null ? (
                <div key={`empty-${i}`} />
              ) : (
                <button
                  key={cell.key}
                  type="button"
                  onClick={() => {
                    onSelect(cell.key);
                    setIsOpen(false);
                  }}
                  className={`aspect-square rounded text-xs font-medium transition-colors ${
                    cell.key === selectedDate
                      ? 'bg-primary text-primary-foreground'
                      : cell.key === todayKey
                      ? 'bg-gray-100 text-gray-900 ring-1 ring-inset ring-gray-300'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {cell.day}
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const LIVE_STATUS_LABELS: Record<string, string> = {
  requesting: 'Requesting…',
  waiting: 'Capturing…',
  completed: 'Captured',
  failed: 'Capture failed',
  timed_out: 'No response',
  device_offline: 'Device offline',
};

/**
 * The camera button in the Screenshots detail header: triggers a live
 * "take a screenshot now" request to this specific device and shows its
 * outcome (elapsed time while waiting; success/failure/offline/timeout
 * once resolved) right next to the button, per the explicit requirement
 * to surface status/timing in the same view rather than a silent action.
 * See useLiveScreenshotRequest for the actual request/socket/poll logic.
 */
function LiveScreenshotButton({
  liveScreenshot,
}: {
  liveScreenshot: ReturnType<typeof useLiveScreenshotRequest>;
}) {
  const { state, trigger, reset } = liveScreenshot;
  const isBusy = state.status === 'requesting' || state.status === 'waiting';
  const isTerminal =
    state.status === 'completed' ||
    state.status === 'failed' ||
    state.status === 'timed_out' ||
    state.status === 'device_offline';

  // Auto-return to idle a couple seconds after resolving, so the button
  // is ready for another capture without requiring an explicit dismiss —
  // long enough to actually read the outcome, short enough to not linger.
  useEffect(() => {
    if (!isTerminal) return;
    const timer = setTimeout(reset, 3000);
    return () => clearTimeout(timer);
  }, [isTerminal, reset]);

  const label = LIVE_STATUS_LABELS[state.status];
  const elapsedLabel = isBusy ? `${(state.elapsedMs / 1000).toFixed(1)}s` : null;

  const statusColorClass =
    state.status === 'completed'
      ? 'text-green-600'
      : state.status === 'failed' || state.status === 'device_offline'
      ? 'text-red-500'
      : state.status === 'timed_out'
      ? 'text-amber-600'
      : 'text-gray-500';

  return (
    <div className="flex items-center gap-1.5">
      {label && (
        <span className={`text-xs font-medium whitespace-nowrap ${statusColorClass}`} role="status">
          {label}
          {elapsedLabel && <span className="text-gray-400 ml-1 font-mono">{elapsedLabel}</span>}
        </span>
      )}
      {state.status === 'failed' && state.errorMessage && (
        <span className="text-xs text-gray-400 max-w-[160px] truncate" title={state.errorMessage}>
          ({state.errorMessage})
        </span>
      )}
      <button
        onClick={trigger}
        disabled={isBusy}
        aria-label="Take a live screenshot now"
        title="Take a live screenshot now"
        className={`p-1.5 rounded transition-colors ${
          isBusy ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
        }`}
      >
        {isBusy ? (
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="13" r="4" strokeWidth="2" />
          </svg>
        )}
      </button>
    </div>
  );
}

function DeviceViewHeader({
  userName,
  userRole,
  employeeName,
  activityPercent,
  selectedDate,
  onDateChange,
  onBack,
  liveScreenshot,
}: {
  userName: string;
  userRole?: string;
  employeeName?: string;
  activityPercent: number;
  selectedDate: string;
  onDateChange: (date: string) => void;
  onBack: () => void;
  liveScreenshot: ReturnType<typeof useLiveScreenshotRequest>;
}) {
  const changeDate = (delta: number) => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    onDateChange(`${yyyy}-${mm}-${dd}`);
  };

  return (
    <div className="flex items-center justify-between pb-3 border-b border-gray-200">
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          aria-label="Back to devices"
          title="Back to devices"
          className="flex items-center gap-1.5 -ml-1.5 pl-1.5 pr-2.5 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M15 19l-7-7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back
        </button>
        <div className="w-px h-6 bg-gray-200" />
        <div className="w-9 h-9 bg-indigo-500 rounded-lg flex items-center justify-center text-white">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <rect x="2" y="3" width="20" height="14" rx="2" strokeWidth="2" />
            <path d="M8 21h8M12 17v4" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <div>
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-sm text-gray-900">{userName}</span>
            {userRole && (
              <span className="text-xs text-gray-500 capitalize">{userRole}</span>
            )}
          </div>
          {employeeName && (
            <div className="text-xs text-gray-500">{employeeName}</div>
          )}
        </div>
        <div className="flex items-center gap-2 ml-4">
          {/* Trend icon */}
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M3 17l6-6 4 4 8-8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="text-xs text-gray-500">Productivity</span>
          <ProductivityDashes percent={activityPercent} />
          <span className="text-sm font-semibold text-gray-700">{activityPercent.toFixed(2)}%</span>
          {/* Info icon */}
          <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <LiveScreenshotButton liveScreenshot={liveScreenshot} />
        {/* Date navigation */}
        <div className="flex items-center gap-1">
          <button
            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            onClick={() => changeDate(-1)}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M15 19l-7-7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <MiniDatePicker selectedDate={selectedDate} onSelect={onDateChange} />
          <button
            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            onClick={() => changeDate(1)}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M9 5l7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function TimelineBar({ timelineData }: { timelineData: any[] }) {
  const hours = useMemo(() => {
    const slots = Array.from({ length: 24 }, (_, i) => ({
      hour: i,
      activityPercent: 0,
    }));
    if (timelineData) {
      for (const entry of timelineData) {
        const ts = new Date(entry.timestamp);
        const h = ts.getHours();
        if (h >= 0 && h < 24) {
          slots[h].activityPercent = entry.activityPercent || 0;
        }
      }
    }
    return slots;
  }, [timelineData]);

  const getColor = (pct: number) => {
    if (pct >= 70) return 'bg-green-500';
    if (pct >= 40) return 'bg-yellow-500';
    if (pct >= 1) return 'bg-orange-400';
    return 'bg-gray-200';
  };

  const formatHourLabel = (h: number): string => {
    if (h === 0) return '12 AM';
    if (h === 12) return '12 PM';
    if (h < 12) return `${h} AM`;
    return `${h - 12} PM`;
  };

  return (
    <div className="mb-4">
      {/* Hour labels on top */}
      <div className="flex">
        {hours.map((slot) => (
          <div key={slot.hour} className="flex-1 text-center">
            <span className="text-[9px] text-gray-400 leading-none">{formatHourLabel(slot.hour)}</span>
          </div>
        ))}
      </div>
      {/* Colored blocks */}
      <div className="flex gap-[1px] h-5 mt-0.5">
        {hours.map((slot) => (
          <div
            key={slot.hour}
            className={`flex-1 rounded-[2px] ${getColor(slot.activityPercent)} transition-colors`}
            title={`${formatHourRange(slot.hour)}: ${slot.activityPercent.toFixed(0)}%`}
          />
        ))}
      </div>
    </div>
  );
}

function OverviewSidebar({ summary }: { summary: any }) {
  const totalActive = summary?.totalActiveSeconds || 0;
  const totalIdle = summary?.totalIdleSeconds || 0;
  const periodStart = summary?.periodStart ? new Date(summary.periodStart) : null;
  const periodEnd = summary?.periodEnd ? new Date(summary.periodEnd) : null;

  const clockInSeconds =
    periodStart && periodEnd
      ? Math.floor((periodEnd.getTime() - periodStart.getTime()) / 1000)
      : 0;

  const trackedSeconds = totalActive + totalIdle;
  const breakSeconds = Math.max(0, clockInSeconds - trackedSeconds);

  const formatDurationPadded = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m`;
  };

  const formatBreakTime = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const stats = [
    { label: 'Clock In Hours', value: formatDurationPadded(clockInSeconds), color: 'border-blue-500' },
    { label: 'Break Time', value: formatBreakTime(breakSeconds), color: 'border-yellow-500' },
    { label: 'Tracked Time', value: formatDurationPadded(totalActive), color: 'border-green-500' },
    { label: 'Idle Time', value: formatDurationPadded(totalIdle), color: 'border-orange-500' },
    { label: 'Lock Time', value: '00h 54m', color: 'border-red-500' },
  ];

  return (
    <div className="w-64 shrink-0">
      <div className="sticky top-4">
        <h3 className="text-base font-semibold text-gray-800 mb-3">Overview</h3>
        <div className="space-y-2">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className={`border-l-[3px] ${stat.color} pl-3 py-1.5`}
            >
              <p className="text-sm font-bold text-gray-800">{stat.value}</p>
              <p className="text-xs text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScreenshotCard({
  screenshot,
  onClick,
}: {
  screenshot: any;
  onClick: () => void;
}) {
  const activity = screenshot.activity;
  const totalSeconds =
    activity ? activity.activeSeconds + activity.idleSeconds : 0;
  const activePct = totalSeconds > 0 ? (activity.activeSeconds / totalSeconds) * 100 : 0;
  const idlePct = totalSeconds > 0 ? (activity.idleSeconds / totalSeconds) * 100 : 0;

  // Generate activity dot segments (10 dots)
  const totalDots = 10;
  const activeDots = Math.round((activePct / 100) * totalDots);
  const idleDots = Math.round((idlePct / 100) * totalDots);
  const emptyDots = totalDots - activeDots - idleDots;

  return (
    <div
      className="cursor-pointer group bg-white rounded border border-gray-200 overflow-hidden hover:border-primary hover:shadow-md transition-all"
      onClick={onClick}
    >
      <div className="relative">
        <img
          src={screenshot.thumbnailUrl}
          alt="Screenshot"
          className="w-full h-24 object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity" />
      </div>
      <div className="px-2 py-1.5">
        <p className="text-[11px] text-gray-600 mb-0.5">
          {activity?.periodStart && activity?.periodEnd ? (
            <>
              {formatTime(activity.periodStart)} - {formatTime(activity.periodEnd)}
              <span className="font-semibold text-gray-700"> ({formatTime(screenshot.timestamp)})</span>
            </>
          ) : (
            formatTime(screenshot.timestamp)
          )}
        </p>
        <div className="flex items-center gap-2 text-[11px] text-gray-500">
          <span className="flex items-center gap-0.5" title="Key presses">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="2" y="6" width="20" height="12" rx="2" strokeWidth="2" />
              <path d="M6 10h0M10 10h0M14 10h0M18 10h0M8 14h8" strokeWidth="2" strokeLinecap="round" />
            </svg>
            {activity?.keyPresses ?? 0}
          </span>
          <span className="flex items-center gap-0.5" title="Mouse clicks">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="2" width="12" height="20" rx="6" strokeWidth="2" />
              <path d="M12 2v8M6 8h12" strokeWidth="2" />
            </svg>
            {activity?.mouseClicks ?? 0}
          </span>
        </div>
        {/* Activity dot segments */}
        <div className="flex gap-[2px] mt-1.5">
          {Array.from({ length: activeDots }, (_, i) => (
            <div key={`a${i}`} className="flex-1 h-[4px] rounded-sm bg-green-500" />
          ))}
          {Array.from({ length: idleDots }, (_, i) => (
            <div key={`i${i}`} className="flex-1 h-[4px] rounded-sm bg-orange-400" />
          ))}
          {Array.from({ length: Math.max(0, emptyDots) }, (_, i) => (
            <div key={`e${i}`} className="flex-1 h-[4px] rounded-sm bg-gray-200" />
          ))}
        </div>
      </div>
    </div>
  );
}

function HourGroup({
  hour,
  screenshots,
  onScreenshotClick,
}: {
  hour: number;
  screenshots: any[];
  onScreenshotClick: (s: any) => void;
}) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-2">
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M12 6v6l4 2" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <h3 className="text-sm font-semibold text-gray-700">{formatHourRange(hour)}</h3>
      </div>
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-2.5">
        {screenshots.map((s: any) => (
          <ScreenshotCard
            key={s.id}
            screenshot={s}
            onClick={() => onScreenshotClick(s)}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main Device Screenshots View ──────────────────────────────

function DeviceScreenshotsView({
  deviceId,
  deviceName,
  employeeName,
  selectedDate,
  onDateChange,
  onBack,
}: {
  deviceId: string;
  deviceName: string;
  employeeName?: string;
  selectedDate: string;
  onDateChange: (date: string) => void;
  onBack: () => void;
}) {
  const [selectedScreenshot, setSelectedScreenshot] = useState<any>(null);
  const liveScreenshot = useLiveScreenshotRequest(deviceId);

  // Fetch all screenshots for the day
  const { data: screenshotData, isLoading: loadingScreenshots } = useScreenshots({
    deviceId,
    startDate: selectedDate,
    endDate: selectedDate,
    page: 1,
    limit: 200,
  });

  // Fetch hourly timeline
  const { data: timelineData } = useQuery({
    queryKey: ['activityTimeline', { deviceId, startDate: selectedDate, endDate: selectedDate, granularity: 'hour' }],
    queryFn: async () => {
      const response = await activityApi.getTimeline({
        deviceId,
        startDate: selectedDate,
        endDate: selectedDate,
        granularity: 'hour',
      });
      return response.data;
    },
  });

  // Fetch daily summary
  const { data: summary } = useQuery({
    queryKey: ['activitySummary', { deviceId, startDate: selectedDate, endDate: selectedDate }],
    queryFn: async () => {
      const response = await activityApi.getSummary({
        deviceId,
        startDate: selectedDate,
        endDate: selectedDate,
      });
      return response.data;
    },
  });

  // Group screenshots by hour (descending)
  const hourGroups = useMemo(() => {
    if (!screenshotData?.data) return [];
    const groups: Record<number, any[]> = {};
    for (const s of screenshotData.data) {
      const h = new Date(s.timestamp).getHours();
      if (!groups[h]) groups[h] = [];
      groups[h].push(s);
    }
    return Object.entries(groups)
      .map(([hour, screenshots]) => ({
        hour: parseInt(hour),
        screenshots: screenshots.sort(
          (a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        ),
      }))
      .sort((a, b) => b.hour - a.hour);
  }, [screenshotData?.data]);

  // Chronological order for the lightbox's Previous/Next — independent of
  // hourGroups' display grouping (which sorts hours descending), so
  // "Next" always means "later in the day" regardless of which hour
  // section the viewer opened a screenshot from.
  const sortedScreenshots = useMemo(() => {
    if (!screenshotData?.data) return [];
    return [...screenshotData.data].sort(
      (a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [screenshotData?.data]);

  const selectedIndex = selectedScreenshot
    ? sortedScreenshots.findIndex((s: any) => s.id === selectedScreenshot.id)
    : -1;

  const goToPreviousScreenshot = () => {
    if (selectedIndex > 0) setSelectedScreenshot(sortedScreenshots[selectedIndex - 1]);
  };

  const goToNextScreenshot = () => {
    if (selectedIndex >= 0 && selectedIndex < sortedScreenshots.length - 1) {
      setSelectedScreenshot(sortedScreenshots[selectedIndex + 1]);
    }
  };

  const activityPercent = summary?.averageActivityPercent ?? 0;
  const trackedSeconds = (summary?.totalActiveSeconds || 0) + (summary?.totalIdleSeconds || 0);
  const trackedFormatted = formatDurationHMS(trackedSeconds);

  // Format date as DD-MM-YYYY for the section label
  const dateLabel = (() => {
    const d = new Date(selectedDate + 'T00:00:00');
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  })();

  return (
    <div className="space-y-0">
      <DeviceViewHeader
        userName={deviceName}
        employeeName={employeeName}
        activityPercent={activityPercent}
        selectedDate={selectedDate}
        onDateChange={onDateChange}
        onBack={onBack}
        liveScreenshot={liveScreenshot}
      />

      {/* Section label bar */}
      <div className="flex items-center justify-between mt-3 mb-1">
        <div className="border-b-2 border-blue-500 pb-1">
          <span className="text-sm font-semibold text-blue-600">Timeline</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Trend icon + dashes */}
          <svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M3 17l6-6 4 4 8-8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <ProductivityDashes percent={activityPercent} />
          <span className="text-xs font-semibold text-gray-700">
            {activityPercent.toFixed(2)}%
          </span>
          <span className="text-xs text-gray-400 mx-0.5">-</span>
          <span className="text-xs font-medium text-gray-600">{trackedFormatted}</span>
          {/* Info icon */}
          <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </div>
      </div>

      <TimelineBar timelineData={timelineData?.data || []} />

      {loadingScreenshots ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : hourGroups.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No screenshots found for this date
        </div>
      ) : (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            {/* Date label */}
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
              <input type="checkbox" className="w-3.5 h-3.5 rounded border-gray-300" />
              <span className="text-xs font-medium text-gray-500">{dateLabel}</span>
            </div>
            {hourGroups.map((group) => (
              <HourGroup
                key={group.hour}
                hour={group.hour}
                screenshots={group.screenshots}
                onScreenshotClick={setSelectedScreenshot}
              />
            ))}
          </div>
          <OverviewSidebar summary={summary} />
        </div>
      )}

      {/* Lightbox */}
      {selectedScreenshot && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedScreenshot(null)}
        >
          <div
            className="relative bg-white rounded-lg max-w-5xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="font-semibold">{selectedScreenshot.device?.name}</h3>
                <p className="text-sm text-gray-500">
                  {formatDateTime(selectedScreenshot.timestamp)}
                </p>
                {selectedScreenshot.activity && (
                  <p className="text-xs text-gray-400 mt-1">
                    Keys: {selectedScreenshot.activity.keyPresses} | Clicks: {selectedScreenshot.activity.mouseClicks} | Active: {formatDuration(selectedScreenshot.activity.activeSeconds)}
                  </p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedScreenshot(null)}>
                Close
              </Button>
            </div>
            {selectedIndex > 0 && (
              <button
                type="button"
                onClick={goToPreviousScreenshot}
                aria-label="Previous screenshot"
                title="Previous screenshot"
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full w-9 h-9 flex items-center justify-center shadow z-10"
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M15 19l-7-7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            {selectedIndex >= 0 && selectedIndex < sortedScreenshots.length - 1 && (
              <button
                type="button"
                onClick={goToNextScreenshot}
                aria-label="Next screenshot"
                title="Next screenshot"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full w-9 h-9 flex items-center justify-center shadow z-10"
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M9 5l7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            <img
              src={selectedScreenshot.url}
              alt="Full screenshot"
              className="w-full"
            />
          </div>
        </div>
      )}
    </div>
  );
}

/** Format seconds as "05H : 11 M : 00 S" */
function formatDurationHMS(totalSeconds: number): string {
  const total = Math.floor(totalSeconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')} H : ${String(m).padStart(2, '0')} M : ${String(s).padStart(2, '0')} S`;
}

// ─── Page Root ─────────────────────────────────────────────────

export default function ScreenshotsPage() {
  const user = useAuthStore((state) => state.user);
  const isManager = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'manager';

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedDevice, setSelectedDevice] = useState<{
    id: string;
    name: string;
    employeeName?: string;
  } | null>(null);

  if (selectedDevice) {
    return (
      <DeviceScreenshotsView
        deviceId={selectedDevice.id}
        deviceName={selectedDevice.name}
        employeeName={selectedDevice.employeeName}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        onBack={() => setSelectedDevice(null)}
      />
    );
  }

  return (
    <DeviceListView
      selectedDate={selectedDate}
      onDateChange={setSelectedDate}
      onSelectDevice={(id, name, employeeName) => setSelectedDevice({ id, name, employeeName })}
      isManager={isManager}
    />
  );
}
